import { create } from "zustand";
import {
  getWhatsAppAccount,
  getConfirmations,
  getConversation,
  getReportsSummary,
  resendConfirmation,
  toggleAIMode,
  sendManualMessage,
  type WhatsAppAccount,
  type Confirmation,
  type Conversation,
  type ConfirmationStatus,
  type ReportsSummary,
} from "../services/whatsapp/whatsappService";

export type FilterTab = "all" | ConfirmationStatus;

interface ChatState {
  // State
  whatsappAccount: WhatsAppAccount | null;
  accountLoading: boolean;
  accountChecked: boolean;
  confirmations: Confirmation[];
  confirmationsLoading: boolean;
  selectedConfirmation: Confirmation | null;
  conversation: Conversation | null;
  conversationLoading: boolean;
  activeFilter: FilterTab;
  searchQuery: string;
  summary: ReportsSummary | null;

  // Actions
  fetchAccount: (ownerId: string) => Promise<void>;
  fetchConfirmations: (
    ownerId: string,
    status?: ConfirmationStatus
  ) => Promise<void>;
  selectConversation: (confirmation: Confirmation) => Promise<void>;
  resendMessage: (confirmationId: number, ownerId: string) => Promise<void>;
  toggleAI: (confirmationId: number, aiEnabled: boolean) => Promise<void>;
  sendMessage: (confirmationId: number, message: string) => Promise<void>;
  refreshConversation: (confirmationId: number) => Promise<void>;
  setFilter: (filter: FilterTab) => void;
  setSearchQuery: (query: string) => void;
  fetchSummary: (ownerId: string) => Promise<void>;
  reset: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  whatsappAccount: null,
  accountLoading: false,
  accountChecked: false,
  confirmations: [],
  confirmationsLoading: false,
  selectedConfirmation: null,
  conversation: null,
  conversationLoading: false,
  activeFilter: "all",
  searchQuery: "",
  summary: null,

  fetchAccount: async (ownerId: string) => {
    set({ accountLoading: true });
    try {
      const account = await getWhatsAppAccount(ownerId);
      if ((account as any).error) {
        set({
          whatsappAccount: null,
          accountLoading: false,
          accountChecked: true,
        });
        return;
      }
      set({
        whatsappAccount: account,
        accountLoading: false,
        accountChecked: true,
      });
    } catch {
      set({
        whatsappAccount: null,
        accountLoading: false,
        accountChecked: true,
      });
    }
  },

  fetchConfirmations: async (
    ownerId: string,
    status?: ConfirmationStatus
  ) => {
    set({ confirmationsLoading: true });
    try {
      const confirmations = await getConfirmations(ownerId, status);
      set({ confirmations, confirmationsLoading: false });
    } catch {
      set({ confirmations: [], confirmationsLoading: false });
    }
  },

  selectConversation: async (confirmation: Confirmation) => {
    set({
      selectedConfirmation: confirmation,
      conversationLoading: true,
    });
    try {
      const conversation = await getConversation(confirmation.id);
      if ((conversation as any).error) {
        set({ conversation: null, conversationLoading: false });
        return;
      }
      set({ conversation, conversationLoading: false });
    } catch {
      set({ conversation: null, conversationLoading: false });
    }
  },

  resendMessage: async (confirmationId: number, ownerId: string) => {
    try {
      await resendConfirmation(confirmationId);
      // Refresh confirmations and selected conversation
      const confirmations = await getConfirmations(ownerId);
      const updated = confirmations.find((c) => c.id === confirmationId);
      set({ confirmations });
      if (updated) {
        set({ selectedConfirmation: updated });
        try {
          const conversation = await getConversation(confirmationId);
          if (!(conversation as any).error) {
            set({ conversation });
          }
        } catch {
          // Non-critical
        }
      }
    } catch (error) {
      throw error;
    }
  },

  toggleAI: async (confirmationId: number, aiEnabled: boolean) => {
    try {
      await toggleAIMode(confirmationId, aiEnabled);
      const currentConversation = useChatStore.getState().conversation;
      if (currentConversation) {
        set({ conversation: { ...currentConversation, ai_enabled: aiEnabled } });
      }
    } catch (error) {
      throw error;
    }
  },

  sendMessage: async (confirmationId: number, message: string) => {
    const currentConversation = useChatStore.getState().conversation;
    if (!currentConversation) return;

    // Optimistic update: show message immediately with "sending" status
    const optimisticMsg = {
      role: "assistant" as const,
      content: message,
      timestamp: new Date().toISOString(),
      status: "sending" as const,
    };
    set({
      conversation: {
        ...currentConversation,
        messages: [...currentConversation.messages, optimisticMsg],
      },
    });

    try {
      const result = await sendManualMessage(confirmationId, message);
      const latest = useChatStore.getState().conversation;
      if (latest) {
        // Replace with server response (status = "sent")
        const serverMessages = result.messages.map((m: any) => ({
          ...m,
          status: m.status || "sent",
        }));
        set({ conversation: { ...latest, messages: serverMessages } });
      }
    } catch {
      // Mark as failed
      const latest = useChatStore.getState().conversation;
      if (latest) {
        const msgs = latest.messages.map((m) =>
          m === optimisticMsg ? { ...m, status: "failed" as const } : m
        );
        set({ conversation: { ...latest, messages: msgs } });
      }
    }
  },

  refreshConversation: async (confirmationId: number) => {
    try {
      const conversation = await getConversation(confirmationId);
      if (!(conversation as any).error) {
        const current = useChatStore.getState().conversation;
        // Only update if there are new messages
        if (current && conversation.messages.length !== current.messages.length) {
          set({ conversation });
        }
      }
    } catch {
      // Silent fail for polling
    }
  },

  setFilter: (filter: FilterTab) => set({ activeFilter: filter }),
  setSearchQuery: (query: string) => set({ searchQuery: query }),

  fetchSummary: async (ownerId: string) => {
    try {
      const summary = await getReportsSummary(ownerId);
      set({ summary });
    } catch {
      set({ summary: null });
    }
  },

  reset: () =>
    set({
      whatsappAccount: null,
      accountChecked: false,
      confirmations: [],
      selectedConfirmation: null,
      conversation: null,
      activeFilter: "all",
      searchQuery: "",
      summary: null,
    }),
}));
